import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { AssignShipmentAction, ShipmentsListType } from '@shared-features/enums';
import { Shipment } from '@features/shared-features/models/shipment.model';

@Component({
  selector: 'app-shipment-assignment-action',
  templateUrl: './shipment-assignment-action.component.html',
  styleUrls: ['./shipment-assignment-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShipmentAssignmentAction implements OnInit, OnChanges, OnDestroy {
  @Input() shipmentId: number;
  @Input() isViewActions: boolean = true;
  @Input() driverRunningShipment: Shipment;
  @Input() truckRunningShipment: Shipment;
  @Input() form: FormGroup;

  @Output() assignShipmentActionChange = new EventEmitter<{
    assignShipmentAction: AssignShipmentAction;
    justification: string;
  }>();

  assignShipmentActionControl: FormControl;

  justificationControl: FormControl;
  RoutesUtil = RoutesUtil;

  protected readonly ShipmentsListType = ShipmentsListType;
  protected readonly AssignShipmentAction = AssignShipmentAction;
  private readonly destroy$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder, private changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    const keys = ['shipmentId', 'driverRunningShipment', 'truckRunningShipment'];
    const hasChanges = keys.some((key) => changes[key] && changes[key].currentValue !== changes[key].previousValue);
    const hasNotRunningShipment = !changes.driverRunningShipment?.currentValue && !changes.truckRunningShipment?.currentValue;

    if (!hasChanges || !this.assignShipmentActionControl) {
      return;
    }

    if (hasNotRunningShipment) {
      this.assignShipmentActionControl.setValue(null);
    }
  }

  ngOnInit(): void {
    this.form.addControl('assignShipmentAction', this.formBuilder.control(null));
    this.form.addControl('justification', this.formBuilder.control(''));

    this.assignShipmentActionControl = this.form.get('assignShipmentAction') as FormControl;
    this.justificationControl = this.form.get('justification') as FormControl;

    this.assignShipmentActionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onAssignShipmentActionChange();
    });
    this.justificationControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.assignShipmentActionChange.emit({
        assignShipmentAction: this.assignShipmentActionControl.value,
        justification: value,
      });
    });

    this.assignShipmentActionControl.patchValue(AssignShipmentAction.Override);
    this.form.valueChanges.subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
  }

  onAssignShipmentActionChange(): void {
    this.justificationControl.reset();
    this.justificationControl.clearValidators();
    if (this.assignShipmentActionControl.value === AssignShipmentAction.Override) {
      this.justificationControl.setValidators(Validators.required);
    }
    this.justificationControl.updateValueAndValidity();
    this.assignShipmentActionChange.emit(this.form.value);
  }

  ngOnDestroy(): void {
    this.justificationControl.clearValidators();
    this.justificationControl.updateValueAndValidity();
    this.assignShipmentActionChange.emit(null);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
